/**
 * Which window the Recall.ai desktop SDK should record. Decided at start time
 * and threaded through start/retry flows as an argument — never cached inside
 * a PreparedRecording, so prewarmed state can be reused for any start.
 *
 * - "detected-meeting": the SDK detected this meeting window itself, so its id
 *   is a genuine Recall window id and is passed to startRecording() directly.
 * - "whole-desktop": every other start (manual, calendar notification,
 *   retry-after-failure). The SDK window id comes from the prewarmed
 *   prepareDesktopRecording() result on the PreparedRecording.
 *
 * Calendar-only notifications carry a synthetic `window.id` minted by the
 * desktop main process (not a Recall window id). Keeping the Recall id in its
 * own variant — only constructible via recordingWindowForMeetingEvent when
 * `wasMeetingDetected` is true — makes it impossible to hand a synthetic id
 * to the SDK.
 *
 * See:
 * - https://docs.recall.ai/docs/desktop-sdk
 * - https://docs.recall.ai/docs/adhoc-meetings-in-person-meetings
 */
export type RecordingWindow =
  | { kind: "detected-meeting"; recallWindowId: string }
  | { kind: "whole-desktop" };

export const WHOLE_DESKTOP_RECORDING_WINDOW: RecordingWindow = { kind: "whole-desktop" };

/**
 * Builds a recording target from a Recall-detected meeting id already trusted
 * by Electron main.
 */
export const recordingWindowForDetectedMeetingWindowId = (windowId: string): RecordingWindow => ({
  kind: "detected-meeting",
  recallWindowId: windowId,
});

/**
 * The single conversion point from a meeting-event payload to a recording
 * window. `wasMeetingDetected` is owned by the desktop main process and is
 * true only for genuine Recall meeting-detection events.
 */
export const recordingWindowForMeetingEvent = ({
  wasMeetingDetected,
  windowId,
}: {
  wasMeetingDetected: boolean;
  windowId: string;
}): RecordingWindow =>
  wasMeetingDetected
    ? { kind: "detected-meeting", recallWindowId: windowId }
    : WHOLE_DESKTOP_RECORDING_WINDOW;
